import type { StudentConceptChipAchievement } from '@mathflat/domain/Achievement'
import type { ConceptChip } from '@mathflat/domain/ConceptChip'
import { CURRICULUM_KEYS } from '@mathflat/domain/Curriculum'
import type { WorkbookOf } from '@mathflat/domain/Workbook'
import { flatMap } from 'lodash-es'
import { makeAutoObservable } from 'mobx'

import { achievementAnalysisController } from '@/controller/achievementAnalysis.controller'
import type { Student } from '@/dto'
import { AchievementConceptChip } from '@/dto/AchievementAnalysis/AchievementConceptChip.dto'
import commonRepo from '@/repository/common.repo'
import type { DateStr } from '@/types'

import type {
  AchievementAnalysisService,
  SchoolTestDataForService,
} from './AchievementAnalysis.service'

const DEFAULT_FILTER_DATA = {
  curriculumKey: CURRICULUM_KEYS.CURRICULUM_KEY_초등학교_1학년_1학기, // 초1
  requestDate: null,
  workbookList: {
    CUSTOM_SIGNATURE: [],
    ACADEMY_GROUP: [],
    PUBLIC: [],
    SCHOOL: [],
  },
  schoolTests: [],
  arithmeticOnly: false,
}

// 현재 성취도와 과거 이력(주차 스냅샷)을 같은 자리에 담는다. 최근 채점 기록은 현재 성취도에만 있다
export type StudentChipAchievementSource = Pick<
  StudentConceptChipAchievement,
  'studentId' | 'conceptChipId' | 'achievementLevel'
> &
  Partial<Pick<StudentConceptChipAchievement, 'latestStudyLog'>>

export class AchievementConceptChipManager {
  private _resConceptChips: ConceptChip[] = []
  private _resAchievements: StudentChipAchievementSource[] = []

  private _achievementChipMap: Map<
    StudentChipAchievementSource['conceptChipId'],
    StudentChipAchievementSource
  > = new Map()

  achievementConceptChips: AchievementConceptChip[] = []

  private _chipCache = new Map<number, AchievementConceptChip>()

  private _filter: {
    // 학년 학기
    curriculumKey: string
    requestDate: DateStr<'-'> | null
    workbookList: {
      [key in keyof AchievementAnalysisService['workbookResponseFilterColls']]: WorkbookOf<key>[]
    }
    schoolTests: SchoolTestDataForService[]
    arithmeticOnly: boolean
  } = DEFAULT_FILTER_DATA

  get filter() {
    return this._filter
  }

  get workbookIds() {
    return flatMap(this._filter.workbookList).map((workbook) => workbook.id)
  }
  get schoolTestIds() {
    // 매쓰플랫인 경우에만 학교별 기출을 반영
    const schoolTestIds = !commonRepo.isSchoolflat
      ? this._filter.schoolTests.map((schoolTest) => schoolTest.id)
      : []
    return schoolTestIds
  }
  constructor() {
    makeAutoObservable<AchievementConceptChipManager, '_chipCache'>(this, { _chipCache: false })
  }

  setChipSources(conceptChips: ConceptChip[], achievements: StudentChipAchievementSource[]) {
    this._resConceptChips = conceptChips
    this._resAchievements = achievements
    this._achievementChipMap = new Map(achievements.map((a) => [a.conceptChipId, a]))
    this.achievementConceptChips = this._buildStudentChips()
  }

  setGroupChips(chips: AchievementConceptChip[]) {
    this.achievementConceptChips = chips
  }

  clearChips() {
    this._resConceptChips = []
    this._resAchievements = []
    this._achievementChipMap = new Map()
    this.achievementConceptChips = []
  }

  private _buildStudentChips(): AchievementConceptChip[] {
    const nextCache = new Map<number, AchievementConceptChip>()
    const chips = this._resConceptChips
      .map((conceptChip) => {
        const achievement = this._achievementChipMap.get(conceptChip.conceptChipId)
        const level = achievement?.achievementLevel ?? 'WHITE'
        const prev = this._chipCache.get(conceptChip.conceptChipId)
        if (
          prev &&
          prev.orderingNumber === conceptChip.orderNumber &&
          prev.achievementLevel === level &&
          prev.studentId === achievement?.studentId &&
          prev.latestStudyLog?.datetime === achievement?.latestStudyLog?.datetime
        ) {
          nextCache.set(conceptChip.conceptChipId, prev)
          return prev
        }
        const made = new AchievementConceptChip({
          id: conceptChip.conceptChipId,
          orderingNumber: conceptChip.orderNumber,
          conceptChipId: conceptChip.conceptChipId,
          conceptChipType: conceptChip.conceptChipType,
          recommended: conceptChip.recommended,
          bigChapterId: conceptChip.bigChapterId,
          bigChapterName: conceptChip.bigChapterName,
          middleChapterId: conceptChip.middleChapterId,
          middleChapterName: conceptChip.middleChapterName,
          littleChapterId: conceptChip.littleChapterId,
          littleChapterName: conceptChip.littleChapterName,
          conceptId: conceptChip.conceptId,
          conceptName: conceptChip.conceptName,
          repProblem: conceptChip.repProblem,
          achievementLevel: level,
          studentId: achievement?.studentId,
          latestStudyLog: achievement?.latestStudyLog,
        })
        nextCache.set(conceptChip.conceptChipId, made)
        return made
      })
      .sort((x, y) => x.orderingNumber - y.orderingNumber)
    this._chipCache = nextCache
    return chips
  }

  updateFilter(data: Partial<AchievementConceptChipManager['_filter']>) {
    this._filter = { ...this._filter, ...data }
  }

  resetFilter() {
    this._filter = DEFAULT_FILTER_DATA
  }

  async fetchAllAndMakeChips(studentId: Student['id']) {
    await this._fetchAchievements(studentId)
    await this._fetchConceptChips()
    this._makeAchievementConceptChipMap()
    this._makeAchievementConceptChips()
  }

  /* --- private fetch method --- */
  // 컨셉칩 fetch (교재 및 학교별 기출을 반영해서)
  private async _fetchConceptChips() {
    const resConceptChips = await achievementAnalysisController.getConceptChips({
      curriculumKey: this._filter.curriculumKey,
      workbookIds: this.workbookIds,
      schoolTestIds: this.schoolTestIds,
      arithmeticOnly: this._filter.arithmeticOnly,
    })
    return (this._resConceptChips = resConceptChips)
  }

  private async _fetchAchievements(studentId: Student['id']) {
    // 조회 날짜가 있는 경우에는 과거 이력 조회 Api 를 호출해야 하고 아닌 경우에는 기본 Api 호출
    if (!this._filter.requestDate) {
      const resAchievements = await achievementAnalysisController.getAchievements(
        {
          curriculumKey: this._filter.curriculumKey,
          arithmeticOnly: this._filter.arithmeticOnly,
        },
        studentId,
      )
      return (this._resAchievements = resAchievements)
    }

    const resAchievements = await achievementAnalysisController.getPastAchievements(
      {
        curriculumKey: this._filter.curriculumKey,
        requestDate: this._filter.requestDate,
        arithmeticOnly: this._filter.arithmeticOnly,
      },
      studentId,
    )

    return (this._resAchievements = resAchievements)
  }

  private _makeAchievementConceptChipMap() {
    this._achievementChipMap = new Map(
      this._resAchievements.map((achievement) => [achievement.conceptChipId, achievement]),
    )
  }

  private _makeAchievementConceptChips() {
    this.achievementConceptChips = this._resConceptChips
      .map((conceptChip) => {
        return new AchievementConceptChip({
          id: conceptChip.conceptChipId,
          orderingNumber: conceptChip.orderNumber,
          conceptChipId: conceptChip.conceptChipId,
          conceptChipType: conceptChip.conceptChipType,
          recommended: conceptChip.recommended,
          bigChapterId: conceptChip.bigChapterId,
          bigChapterName: conceptChip.bigChapterName,
          middleChapterId: conceptChip.middleChapterId,
          middleChapterName: conceptChip.middleChapterName,
          littleChapterId: conceptChip.littleChapterId,
          littleChapterName: conceptChip.littleChapterName,
          conceptId: conceptChip.conceptId,
          conceptName: conceptChip.conceptName,
          repProblem: conceptChip.repProblem,
          achievementLevel:
            this._achievementChipMap.get(conceptChip.conceptChipId)?.achievementLevel ?? 'WHITE',
          studentId: this._achievementChipMap.get(conceptChip.conceptChipId)?.studentId,
          latestStudyLog: this._achievementChipMap.get(conceptChip.conceptChipId)?.latestStudyLog,
        })
      })
      .sort((x, y) => x.orderingNumber - y.orderingNumber)
  }
}
